import AlertDialog from '@/components/dashboard/alert-dialog';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import { useMemo } from 'react';
import DateRangeSelect from './orders/_components/common/DateRangeSelect';
import MarketTabSectionReview from './orders/_components/common/MarketTabSectionReview';
import ContentsTab from './orders/_components/inquiries/ContentsTab';
import EditTemplate from './orders/_components/inquiries/EditTemplate';
import InquiryFilterSelect from './orders/_components/inquiries/InquiryFilterSelect';
import { InquiryProvider, useInquiry } from './orders/_components/inquiries/InquiryProvider';
import TableSection from './orders/_components/inquiries/TableSection';
import TabSection, { ENABLE_CHECK_INQUIRY } from './orders/_components/inquiries/TabSection';
import { MarketStoreListItem } from './orders/_consts/constants';
import {
  LayoutOrderContainer,
  LayoutReviewBodyContent,
  LayoutStickySection,
  LayoutTabSection,
} from './orders/_layouts/LayoutOrderContainer';
import {
  LayoutReviewControllerContent,
  LayoutReviewControllerSection,
  LayoutReviewScrollArea,
} from './orders/_layouts/LayoutsReviews';
import { getMarketKey } from './orders/_utils/getMarketKey';
import {
  filterInquiriesByDate,
  filterInquiriesByTab,
  getTabListWithCounts,
  sortInquiries,
} from './orders/_utils/inquiry';
import { membershipInquiryPreviewMarketCounts } from './orders/_components/membership-review-inquiry-mock';

// 페이지 컴포넌트
const PCInquiryListPage = () => {
  const isPreviewMode = useMembershipPreviewMode();

  return (
    <InquiryProvider>
      {!isPreviewMode && <AlertDialog />}
      <InquiryContent />
    </InquiryProvider>
  );
};

export default PCInquiryListPage;

// 메인 Inquiry Content
const InquiryContent = () => {
  const {
    currentTab,
    marketList,
    selectedMarket,
    setSelectedMarket,
    startDate,
    endDate,
    handleStartDate,
    handleEndDate,
    filterSelect,
    inquiryByMarketKey,
    inquiryList,
    isLoading,
  } = useInquiry();
  const isPreviewMode = useMembershipPreviewMode();
  // "전체" 마켓인지 체크
  const selectedKey = selectedMarket
    ? getMarketKey({ storeId: selectedMarket.StoreId, marketId: selectedMarket.MarketId })
    : '';
  const isAllMarket =
    selectedMarket && (selectedMarket.MarketId === 0 || selectedMarket.MarketNameEnglish === 'ALL');

  // 마켓별 문의 개수 (전체는 inquiryList 전체 사용)
  const marketOrderCounts = useMemo(() => {
    if (isPreviewMode) return membershipInquiryPreviewMarketCounts;

    return marketList.map((market: MarketStoreListItem) => {
      const key = getMarketKey({ marketId: market.MarketId, storeId: market.StoreId });
      const isAll = market.MarketId === 0 || market.MarketNameEnglish === 'ALL';
      const baseOrders = isAll ? inquiryList : inquiryByMarketKey[key] ?? [];

      // 미답변(CheckedYn !== 'Y')인 문의만 카운트
      const unAnsweredCount = baseOrders.filter((item) => item.CheckedYn !== 'Y').length;

      return {
        key,
        orderCount: unAnsweredCount, // "미답변" 개수만
      };
    });
  }, [isPreviewMode, marketList, inquiryByMarketKey, inquiryList]);

  // 탭별 카운트
  const tabListWithCounts = useMemo(() => {
    const baseOrders = isAllMarket ? inquiryList : inquiryByMarketKey[selectedKey] || [];
    const dateFiltered = filterInquiriesByDate(baseOrders, startDate, endDate);
    return getTabListWithCounts(dateFiltered);
  }, [isAllMarket, inquiryList, inquiryByMarketKey, selectedKey, startDate, endDate]);

  // 실제 표시할 테이블 데이터
  const filteredTableOrderList = useMemo(() => {
    if (
      !selectedMarket ||
      (!isAllMarket && !ENABLE_CHECK_INQUIRY.includes(selectedMarket.MarketNameEnglish))
    ) {
      return [];
    }
    const baseOrders = isAllMarket ? inquiryList : inquiryByMarketKey[selectedKey] || [];
    const dateFiltered = filterInquiriesByDate(baseOrders, startDate, endDate);
    const sorted = sortInquiries(dateFiltered, filterSelect.value);
    return filterInquiriesByTab(sorted, currentTab);
  }, [
    isAllMarket,
    inquiryList,
    inquiryByMarketKey,
    selectedKey,
    selectedMarket,
    startDate,
    endDate,
    filterSelect,
    currentTab,
  ]);

  return (
    <LayoutOrderContainer>
      <LayoutTabSection>
        <TabSection />
      </LayoutTabSection>
      <LayoutStickySection className="mt-4 max-w-full pb-[0px] pr-[60px]">
        {marketList.length > 1 && (
          <MarketTabSectionReview
            marketList={marketList}
            selectedMarket={selectedMarket}
            setSelectedMarket={setSelectedMarket}
            marketOrderCounts={marketOrderCounts}
          />
        )}
        <LayoutReviewControllerSection>
          <LayoutReviewControllerContent>
            <LayoutReviewScrollArea>
              <DateRangeSelect
                startDate={startDate}
                handleStartDate={handleStartDate}
                endDate={endDate}
                handleEndDate={handleEndDate}
              />
              <InquiryFilterSelect />
            </LayoutReviewScrollArea>
            <EditTemplate />
          </LayoutReviewControllerContent>
          <ContentsTab tabListWithCounts={tabListWithCounts} />
        </LayoutReviewControllerSection>
      </LayoutStickySection>
      <LayoutReviewBodyContent>
        <TableSection isLoading={isLoading} tableOrderList={filteredTableOrderList} />
      </LayoutReviewBodyContent>
    </LayoutOrderContainer>
  );
};
